import React, { useState, useEffect } from 'react';
import { CreditCard, Save, Unlink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Badge } from '@/app/components/ui/badge';
import { toast } from '@/app/components/ui/simple-toaster';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

interface PaymentsConfig {
  stripe_connect_account_id: string;
  is_connected: boolean;
}

function usePaymentsConfig() {
  return useQuery({
    queryKey: ['organizations', 'payments'],
    queryFn: async (): Promise<PaymentsConfig> => {
      const response = await apiClient.get<PaymentsConfig>('/api/v1/organizations/payments/');
      return response.data;
    },
    staleTime: 1000 * 60 * 5,
  });
}

function useUpdatePaymentsConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { stripe_connect_account_id: string }): Promise<PaymentsConfig> => {
      const response = await apiClient.patch<PaymentsConfig>('/api/v1/organizations/payments/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations', 'payments'] });
    },
  });
}

export function PaymentsCard() {
  const { data: config, isLoading } = usePaymentsConfig();
  const updateConfig = useUpdatePaymentsConfig();
  const [accountId, setAccountId] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (config) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAccountId(config.stripe_connect_account_id || '');
    }
  }, [config]);

  useEffect(() => {
    if (config) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasChanges(accountId !== (config.stripe_connect_account_id || ''));
    }
  }, [accountId, config]);

  const handleSave = async () => {
    try {
      await updateConfig.mutateAsync({ stripe_connect_account_id: accountId });
      toast.success('Payments Settings Saved', {
        description: accountId
          ? 'Stripe Connect account has been linked.'
          : 'Stripe Connect account has been disconnected.',
      });
    } catch {
      toast.error('Error', {
        description: 'Failed to save payments settings. Ensure the account ID starts with "acct_".',
      });
    }
  };

  const handleDisconnect = async () => {
    setAccountId('');
    try {
      await updateConfig.mutateAsync({ stripe_connect_account_id: '' });
      toast.success('Disconnected', {
        description: 'Stripe Connect account has been disconnected. Payments will use the platform account.',
      });
    } catch {
      toast.error('Error', {
        description: 'Failed to disconnect Stripe account.',
      });
    }
  };

  if (isLoading) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Payments
          {config?.is_connected && (
            <Badge className="bg-green-100 text-green-800 ml-2">Connected</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Connect your Stripe account to receive invoice payments directly.
          Without a connected account, payments are collected by the platform on your behalf.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="stripe-connect-id">Stripe Connect Account ID</Label>
          <div className="flex gap-2">
            <Input
              id="stripe-connect-id"
              placeholder="acct_1234567890"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="max-w-sm font-mono"
            />
            {hasChanges && (
              <Button onClick={handleSave} disabled={updateConfig.isPending}>
                <Save className="h-4 w-4 mr-2" />
                {updateConfig.isPending ? 'Saving...' : 'Save'}
              </Button>
            )}
            {config?.is_connected && !hasChanges && (
              <Button
                variant="outline"
                onClick={handleDisconnect}
                disabled={updateConfig.isPending}
              >
                <Unlink className="h-4 w-4 mr-2" />
                Disconnect
              </Button>
            )}
          </div>
          <p className="text-sm text-gray-500">
            Enter your Stripe Connect account ID (starts with <code>acct_</code>).
            You can find this in your{' '}
            <a
              href="https://dashboard.stripe.com/settings/account"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Stripe Dashboard
            </a>.
          </p>
        </div>

        {!config?.is_connected && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
            <p className="text-sm text-amber-800">
              <strong>No Stripe account connected.</strong> Invoice payments from clients
              will be collected by H3ight on your behalf and flagged accordingly.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
